// ================================================
// STAKEHOLDER COMMUNICATION TRACKER PANEL
// Feature 5 - Track external stakeholder relationships
// ================================================
import React, { useState, useMemo } from 'react';
import { 
  Users, MessageSquare, Calendar, AlertTriangle,
  CheckCircle2, Clock, Plus, ChevronDown, ChevronUp,
  Send, Building2, Briefcase, DollarSign, Globe
} from 'lucide-react';
import { CHIEF_OF_STAFF_COLORS } from '../focus';
import { stakeholders, formatDate, getDaysUntil } from '../../data/chiefOfStaffData';

const StakeholderPanel = () => {
  const [expandedStakeholder, setExpandedStakeholder] = useState(null);
  const [showAddInteraction, setShowAddInteraction] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'calendar'
  const [interactionNote, setInteractionNote] = useState('');
  const [nextFollowUp, setNextFollowUp] = useState('');

  // Group stakeholders by health status
  const groupedStakeholders = useMemo(() => {
    return {
      needs_attention: stakeholders.filter(s => s.healthStatus === 'needs_attention'),
      followup_due: stakeholders.filter(s => s.healthStatus === 'followup_due'),
      healthy: stakeholders.filter(s => s.healthStatus === 'healthy')
    };
  }, []);

  const typeConfig = {
    'Board Member': { icon: Building2, color: '#8B5CF6' },
    'Strategic Partner': { icon: Briefcase, color: '#3B82F6' },
    'Investor': { icon: DollarSign, color: '#10B981' },
    'Key Client': { icon: Users, color: '#F59E0B' },
    'Community Partner': { icon: Users, color: '#EC4899' },
    'Government': { icon: Globe, color: '#6366F1' }
  };

  const healthConfig = {
    needs_attention: { label: 'Needs Attention', color: '#EF4444', bgColor: '#EF444420' },
    followup_due: { label: 'Follow-Up Due', color: '#F59E0B', bgColor: '#F59E0B20' },
    healthy: { label: 'Healthy', color: '#10B981', bgColor: '#10B98120' }
  };

  const generateDraftMessage = (stakeholder) => {
    const daysSince = Math.floor((new Date() - new Date(stakeholder.lastContact)) / (1000 * 60 * 60 * 24));
    let message = '';
    
    if (daysSince > 14) {
      message = `Dear ${stakeholder.name.split(' ')[0]},\n\nI wanted to touch base and share a brief update on our progress. It's been a few weeks since we last connected, and I thought you might appreciate a quick overview of recent developments.\n\nI'd also love to schedule a time to discuss any questions you might have.\n\nBest regards`;
    } else {
      message = `Hi ${stakeholder.name.split(' ')[0]},\n\nFollowing up on our recent conversation about ${stakeholder.lastTopic}.\n\nI wanted to provide a quick update and see if there's anything additional you need from our side.\n\nLooking forward to hearing from you.`;
    }
    
    return message;
  };

  const handleContact = (stakeholder) => {
    const message = generateDraftMessage(stakeholder);
    alert(`Draft message to ${stakeholder.name}:\n\n${message}`);
  };

  const handleLogInteraction = (stakeholderId) => {
    if (!interactionNote) return;
    alert(`Logged interaction with stakeholder:\nNote: ${interactionNote}\nNext follow-up: ${nextFollowUp || 'Not set'}`);
    setInteractionNote('');
    setNextFollowUp('');
    setShowAddInteraction(null);
  };

  // Calendar view data
  const calendarData = useMemo(() => {
    const weeks = [];
    const today = new Date();
    const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    
    for (let i = 0; i < 28; i++) {
      const date = new Date(startOfMonth.getTime() + i * 24 * 60 * 60 * 1000);
      const followUps = stakeholders.filter(s => {
        const followUpDate = new Date(s.nextFollowUp);
        return followUpDate.toDateString() === date.toDateString();
      });
      weeks.push({ date, followUps });
    }
    
    return weeks;
  }, []);

  return (
    <div className="p-5 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Users className="w-5 h-5" style={{ color: CHIEF_OF_STAFF_COLORS.primary }} />
          <div>
            <h3 className="text-sm font-bold text-white">{stakeholders.length} Stakeholders</h3>
            <p className="text-xs text-gray-400">
              {groupedStakeholders.needs_attention.length} need attention
            </p>
          </div>
        </div>
        <div className="flex gap-1 p-1 rounded-lg" style={{ backgroundColor: '#0D1117' }}>
          <button
            onClick={() => setViewMode('list')}
            className="px-3 py-1.5 rounded text-xs font-medium transition-all"
            style={{ 
              backgroundColor: viewMode === 'list' ? CHIEF_OF_STAFF_COLORS.primary : 'transparent',
              color: viewMode === 'list' ? 'white' : '#9CA3AF'
            }}
          >
            List
          </button>
          <button
            onClick={() => setViewMode('calendar')}
            className="px-3 py-1.5 rounded text-xs font-medium transition-all"
            style={{ 
              backgroundColor: viewMode === 'calendar' ? CHIEF_OF_STAFF_COLORS.primary : 'transparent',
              color: viewMode === 'calendar' ? 'white' : '#9CA3AF'
            }}
          >
            Calendar
          </button>
        </div>
      </div>

      {viewMode === 'list' ? (
        /* List View */
        <div className="space-y-4">
          {Object.entries(groupedStakeholders).map(([status, statusStakeholders]) => {
            if (statusStakeholders.length === 0) return null;
            const config = healthConfig[status];
            
            return (
              <div key={status} className="space-y-2">
                <h3 
                  className="text-xs font-bold uppercase tracking-wider flex items-center gap-2"
                  style={{ color: config.color }}
                >
                  {status === 'needs_attention' && <AlertTriangle className="w-3 h-3" />}
                  {status === 'followup_due' && <Clock className="w-3 h-3" />}
                  {status === 'healthy' && <CheckCircle2 className="w-3 h-3" />}
                  {config.label} ({statusStakeholders.length})
                </h3>
                
                {statusStakeholders.map((stakeholder) => {
                  const typeInfo = typeConfig[stakeholder.type] || typeConfig['Key Client'];
                  const TypeIcon = typeInfo.icon;
                  const isExpanded = expandedStakeholder === stakeholder.id;
                  
                  return (
                    <div 
                      key={stakeholder.id}
                      className="rounded-xl overflow-hidden"
                      style={{ 
                        backgroundColor: '#161B22',
                        borderLeft: `4px solid ${config.color}`
                      }}
                    >
                      {/* Stakeholder Card */}
                      <div 
                        className="p-4 cursor-pointer"
                        onClick={() => setExpandedStakeholder(isExpanded ? null : stakeholder.id)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div 
                              className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold text-white"
                              style={{ backgroundColor: typeInfo.color }}
                            >
                              {stakeholder.avatar}
                            </div>
                            <div>
                              <p className="text-sm font-medium text-white">{stakeholder.name}</p>
                              <div className="flex items-center gap-2 text-xs text-gray-400">
                                <TypeIcon className="w-3 h-3" />
                                <span>{stakeholder.organization}</span>
                                <span>•</span>
                                <span>{stakeholder.type}</span>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="text-right">
                              <p className="text-xs text-gray-400">Health Score</p>
                              <p 
                                className="text-lg font-bold"
                                style={{ color: config.color }}
                              >
                                {stakeholder.healthScore}
                              </p>
                            </div>
                            {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                          </div>
                        </div>
                        
                        {/* Quick info */}
                        <div className="flex items-center gap-4 mt-3 text-xs text-gray-400">
                          <span>Last contact: {formatDate(stakeholder.lastContact)}</span>
                          <span>•</span>
                          <span 
                            style={{ color: new Date(stakeholder.nextFollowUp) < new Date() ? '#EF4444' : '#10B981' }}
                          >
                            {getDaysUntil(stakeholder.nextFollowUp)}
                          </span>
                        </div>
                      </div>

                      {/* Expanded Details */}
                      {isExpanded && (
                        <div className="border-t border-white/10 p-4 space-y-4">
                          {/* Last Topic */}
                          <div>
                            <p className="text-xs text-gray-500 mb-1">Last Discussion</p>
                            <p className="text-sm text-white">{stakeholder.lastTopic}</p>
                          </div>
                          
                          {/* Notes */}
                          <div>
                            <p className="text-xs text-gray-500 mb-1">Notes</p>
                            <p className="text-sm text-gray-300">{stakeholder.notes}</p>
                          </div>
                          
                          {/* Actions */}
                          <div className="flex gap-2">
                            <button
                              onClick={(e) => { e.stopPropagation(); handleContact(stakeholder); }}
                              className="flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-medium"
                              style={{ backgroundColor: CHIEF_OF_STAFF_COLORS.primary, color: 'white' }}
                            >
                              <MessageSquare className="w-4 h-4" />
                              Contact
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); setShowAddInteraction(stakeholder.id); }}
                              className="flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-medium bg-white/10 text-white"
                            >
                              <Plus className="w-4 h-4" />
                              Log Interaction
                            </button>
                          </div>
                          
                          {/* Add Interaction Form */}
                          {showAddInteraction === stakeholder.id && (
                            <div className="pt-3 border-t border-white/10 space-y-2">
                              <textarea
                                placeholder="What was discussed..."
                                value={interactionNote}
                                onChange={(e) => setInteractionNote(e.target.value)}
                                className="w-full p-3 rounded-lg text-sm text-white placeholder-gray-500 resize-none"
                                style={{ backgroundColor: '#0D1117', border: `1px solid ${CHIEF_OF_STAFF_COLORS.primary}20` }}
                                rows={2}
                              />
                              <div className="flex gap-2">
                                <input
                                  type="date"
                                  value={nextFollowUp}
                                  onChange={(e) => setNextFollowUp(e.target.value)}
                                  placeholder="Next follow-up"
                                  className="flex-1 p-2 rounded-lg text-sm text-white"
                                  style={{ backgroundColor: '#0D1117', border: `1px solid ${CHIEF_OF_STAFF_COLORS.primary}20` }}
                                />
                                <button
                                  onClick={() => handleLogInteraction(stakeholder.id)}
                                  className="px-4 py-2 rounded-lg text-sm font-medium"
                                  style={{ backgroundColor: CHIEF_OF_STAFF_COLORS.primary, color: 'white' }}
                                >
                                  Save
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      ) : (
        /* Calendar View */
        <div 
          className="rounded-xl p-4"
          style={{ backgroundColor: '#161B22', border: `1px solid ${CHIEF_OF_STAFF_COLORS.primary}20` }}
        >
          <h4 className="text-sm font-bold text-white mb-4 flex items-center gap-2">
            <Calendar className="w-4 h-4" style={{ color: CHIEF_OF_STAFF_COLORS.primary }} />
            Follow-Up Calendar
          </h4>
          <div className="grid grid-cols-7 gap-1">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
              <div key={day} className="text-center text-xs text-gray-500 py-2">
                {day}
              </div>
            ))}
            {calendarData.map((day, idx) => {
              const isToday = day.date.toDateString() === new Date().toDateString();
              const hasFollowUps = day.followUps.length > 0;
              
              return (
                <div 
                  key={idx}
                  className="aspect-square flex flex-col items-center justify-center rounded-lg text-xs relative"
                  style={{ 
                    backgroundColor: isToday ? CHIEF_OF_STAFF_COLORS.primary + '20' : hasFollowUps ? '#EF444420' : 'transparent',
                    border: isToday ? `1px solid ${CHIEF_OF_STAFF_COLORS.primary}` : 'none'
                  }}
                >
                  <span className={isToday ? 'text-white font-bold' : 'text-gray-400'}>
                    {day.date.getDate()}
                  </span>
                  {hasFollowUps && (
                    <span 
                      className="absolute bottom-1 w-4 h-4 rounded-full flex items-center justify-center text-[10px] font-bold"
                      style={{ backgroundColor: '#EF4444', color: 'white' }}
                    >
                      {day.followUps.length}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default StakeholderPanel;
